import moment from 'moment';
import * as XLSX from 'xlsx';

/**
 * Line Item Field Definitions matching Ad Ops CSV format
 * These are the exact column names from their Budget & Strategy exports
 */
export const LINE_ITEM_FIELDS = {
  name: {
    key: 'name',
    label: 'Line Item Name',
    required: true,
    type: 'string',
    csvColumns: ['Line Item Name', 'Name', 'LI Name', 'Line Item'],
  },
  startDate: {
    key: 'startDate',
    label: 'Start Date',
    required: false,
    type: 'date',
    csvColumns: ['Start Date', 'Start', 'Flight Start', 'Begin Date'],
  },
  endDate: {
    key: 'endDate',
    label: 'End Date',
    required: false,
    type: 'date',
    csvColumns: ['End Date', 'End', 'Flight End', 'Finish Date'],
  },
  budgetTotal: {
    key: 'budgetTotal',
    label: 'Budget',
    required: false,
    type: 'number',
    csvColumns: ['DSP Spend', 'Budget', 'Spend', 'Amount', 'Budget Total'],
  },
  budgetPercentage: {
    key: 'budgetPercentage',
    label: 'Allocation %',
    required: false,
    type: 'number',
    csvColumns: ['Allocation%', 'Allocation', 'Budget %', 'Percentage', '%'],
  },
  cpm: {
    key: 'cpm',
    label: 'Bid/CPM',
    required: false,
    type: 'number',
    csvColumns: ['Bid', 'CPM', 'Rate', 'Cost Per Mille'],
  },
  segments: {
    key: 'segments',
    label: 'Segments',
    required: false,
    type: 'string',
    csvColumns: ['Segments', 'Segment'],
  },
  segmentId: {
    key: 'segmentId',
    label: 'Segment ID',
    required: false,
    type: 'string',
    csvColumns: ['Segment ID', 'SegmentID', 'Segment Id'],
  },
  dealName: {
    key: 'dealName',
    label: 'Deal Name',
    required: false,
    type: 'string',
    csvColumns: ['Deal Name', 'Deal Names', 'Deals'],
  },
  dealId: {
    key: 'dealId',
    label: 'Deal ID',
    required: false,
    type: 'string',
    csvColumns: ['ID', 'Deal ID', 'Deal IDs', 'DealID'],
  },
};

/**
 * Auto-detect column mappings based on CSV headers
 * Matches headers to field definitions using exact and fuzzy matching
 */
export const autoDetectColumnMappings = (headers) => {
  const mappings = {};

  headers.forEach((header) => {
    const normalizedHeader = header.toLowerCase().trim();
    let matchedField = null;
    let confidence = 0;

    // Try each field definition
    Object.entries(LINE_ITEM_FIELDS).forEach(([fieldKey, fieldDef]) => {
      // Check each possible CSV column name
      fieldDef.csvColumns.forEach((csvCol) => {
        const normalizedCol = csvCol.toLowerCase().trim();

        // Exact match
        if (normalizedHeader === normalizedCol) {
          if (confidence < 1.0) {
            matchedField = fieldKey;
            confidence = 1.0;
          }
        }
        // Header contains the column name
        else if (normalizedHeader.includes(normalizedCol) || normalizedCol.includes(normalizedHeader)) {
          const similarity = Math.min(normalizedHeader.length, normalizedCol.length) /
                           Math.max(normalizedHeader.length, normalizedCol.length);
          if (similarity * 0.9 > confidence) {
            matchedField = fieldKey;
            confidence = similarity * 0.9;
          }
        }
      });
    });

    mappings[header] = {
      field: matchedField,
      confidence: confidence,
    };
  });

  return mappings;
};

/**
 * Generate Excel template file for line item import
 */
export const downloadLineItemTemplate = () => {
  const headers = [
    'Line Item Name',
    'Start Date',
    'End Date',
    'DSP Spend',
    'Allocation%',
    'Bid',
    'Segments',
    'Segment ID',
    'Deal Name',
    'ID',
  ];

  const tomorrow = moment().add(1, 'days').format('M/D/YYYY');
  const nextMonth = moment().add(30, 'days').format('M/D/YYYY');

  const sampleRows = [
    [
      'CPM_BT_Example_DIS_Prospecting',
      tomorrow,
      nextMonth,
      '$500.00',
      '25%',
      '$8.00',
      'ExampleSegment_21Plus',
      '1234567890',
      'Example_PMP_Deal',
      'PM-XXXX-0001',
    ],
    [
      'CPM_BT_AnotherExample_DIS_Prospecting',
      tomorrow,
      nextMonth,
      '$300.00',
      '15%',
      '$7.50',
      '',
      '',
      '',
      '',
    ],
  ];

  const wsData = [headers, ...sampleRows];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths
  const columnWidths = [
    { wch: 45 }, // Line Item Name
    { wch: 12 }, // Start Date
    { wch: 12 }, // End Date
    { wch: 12 }, // DSP Spend
    { wch: 12 }, // Allocation%
    { wch: 10 }, // Bid
    { wch: 40 }, // Segments
    { wch: 15 }, // Segment ID
    { wch: 50 }, // Deal Name
    { wch: 20 }, // ID
  ];
  ws['!cols'] = columnWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Line Item Import');

  XLSX.writeFile(wb, `line_item_import_template_${moment().format('YYYY-MM-DD')}.xlsx`);
};

/**
 * Get sheet names from an Excel file
 * @param {File} file - The Excel file
 * @returns {Promise<string[]>} - Array of sheet names
 */
export const getExcelSheetNames = (file) => {
  return new Promise((resolve, reject) => {
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      resolve([]);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        resolve(workbook.SheetNames);
      } catch (error) {
        reject(new Error(`Failed to read sheet names: ${error.message}`));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Parse Excel sheet data and find the header row
 * Some Excel files have metadata rows before the actual headers
 * @param {Array} jsonData - Raw sheet data as array of arrays
 * @returns {{ headers: string[], dataRows: Array, headerRowIndex: number }}
 */
const findHeaderRowAndData = (jsonData) => {
  // Look for the row that looks most like headers (has multiple non-empty string values)
  for (let i = 0; i < Math.min(5, jsonData.length); i++) {
    const row = jsonData[i];
    if (!row || row.length === 0) continue;

    // Count non-empty cells that look like headers (strings, not numbers)
    const headerLikeCells = row.filter(cell => {
      const val = String(cell || '').trim();
      return val && isNaN(Number(val)) && val.length < 50;
    });

    // If this row has multiple header-like values, it's probably the header row
    if (headerLikeCells.length >= 3) {
      return {
        headers: row.map(h => String(h).trim()),
        dataRows: jsonData.slice(i + 1),
        headerRowIndex: i,
      };
    }
  }

  // Fallback to first row as header
  return {
    headers: jsonData[0]?.map(h => String(h).trim()) || [],
    dataRows: jsonData.slice(1),
    headerRowIndex: 0,
  };
};

/**
 * Parse CSV or Excel file and return array of row objects
 * @param {File} file - The file to parse
 * @param {string} [sheetName] - Optional sheet name for Excel files (defaults to first sheet)
 * @returns {Promise<{ rows: Array, headers: string[], columnMappings: Object, sheetNames: string[], selectedSheet: string }>}
 */
export const parseLineItemFile = (file, sheetName = null) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

    reader.onload = (e) => {
      try {
        let headers = [];
        let dataRows = [];
        let sheetNames = [];
        let selectedSheet = null;

        if (isExcel) {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          sheetNames = workbook.SheetNames;

          // Select the requested sheet or default to first
          selectedSheet = sheetName && sheetNames.includes(sheetName)
            ? sheetName
            : sheetNames[0];

          const sheet = workbook.Sheets[selectedSheet];
          const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

          if (jsonData.length < 2) {
            reject(new Error('Sheet is empty or contains only headers'));
            return;
          }

          // Find the actual header row (skip metadata rows)
          const { headers: foundHeaders, dataRows: foundDataRows } = findHeaderRowAndData(jsonData);
          headers = foundHeaders;
          dataRows = foundDataRows;
        } else {
          const text = e.target.result;
          const lines = text.split('\n').filter((line) => {
            const trimmed = line.trim();
            return trimmed && !trimmed.startsWith('#');
          });

          if (lines.length < 2) {
            reject(new Error('File is empty or contains only headers'));
            return;
          }

          headers = parseCSVLine(lines[0]);
          dataRows = lines.slice(1).map(line => parseCSVLine(line));
        }

        // Filter out empty headers
        headers = headers.filter(h => h && h.trim());

        // Auto-detect column mappings (rule-based, will be enhanced by AI)
        const columnMappings = autoDetectColumnMappings(headers);

        const rows = [];
        dataRows.forEach((values, index) => {
          // Skip empty rows or rows that start with underscores (separator rows)
          const firstValue = String(values[0] || '').trim();
          if (!firstValue || firstValue.startsWith('_')) {
            return;
          }

          if (values && values.some((val) => String(val).trim())) {
            const rowData = {};
            headers.forEach((header, headerIndex) => {
              let cellValue = String(values[headerIndex] || '').trim();
              rowData[header] = cellValue;
            });
            rows.push({ lineNumber: index + 2, data: rowData });
          }
        });

        if (rows.length === 0) {
          reject(new Error('Sheet contains no valid data rows'));
          return;
        }

        resolve({
          rows,
          headers,
          columnMappings,
          sheetNames,
          selectedSheet,
        });
      } catch (error) {
        reject(new Error(`Failed to parse file: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    if (isExcel) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  });
};

/**
 * Parse a single CSV line handling quoted values
 */
const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"' && inQuotes && nextChar === '"') {
      current += '"';
      i++;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current);
  return result.map((val) => val.trim());
};

/**
 * Parse date in supported formats only (M/D/YYYY, MM/DD/YYYY, YYYY-MM-DD)
 * Returns null for unrecognized formats to avoid ambiguous parsing
 */
const parseDate = (dateStr) => {
  if (!dateStr) return null;

  const trimmed = String(dateStr).trim();

  // Try M/D/YYYY or MM/DD/YYYY format first (Ad Ops format)
  const mdyMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdyMatch) {
    const [, month, day, year] = mdyMatch;
    const parsed = moment(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed : null;
  }

  // Try YYYY-MM-DD format (ISO format)
  const ymdMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymdMatch) {
    const parsed = moment(trimmed, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed : null;
  }

  // No fallback - return null for unrecognized formats
  return null;
};

/**
 * Parse currency/number value (handles $, %, commas)
 */
const parseNumber = (value) => {
  if (!value) return null;
  const cleaned = String(value).replace(/[$,%\s]/g, '').replace(/,/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
};

/**
 * Parse percentage value - handles both decimal (0.3 = 30%) and percentage (30 = 30%) formats
 * Returns value as percentage (30 for 30%)
 */
const parsePercentage = (value) => {
  if (!value) return null;
  const num = parseNumber(value);
  if (num === null) return null;

  // If value is <= 1 and doesn't contain %, it's likely a decimal (0.3 = 30%)
  // Convert to percentage by multiplying by 100
  const originalStr = String(value).trim();
  const hasPercentSign = originalStr.includes('%');

  if (!hasPercentSign && num > 0 && num <= 1) {
    return num * 100;
  }

  return num;
};

/**
 * Validate a single line item row
 */
export const validateLineItemRow = (rowData, lineNumber, columnMappings) => {
  const errors = [];
  const warnings = [];

  const getMappedValue = (fieldKey) => {
    const csvColumn = Object.keys(columnMappings).find(
      col => columnMappings[col]?.field === fieldKey
    );
    return csvColumn ? rowData[csvColumn]?.trim() : null;
  };

  // Validate name (required)
  const name = getMappedValue('name');
  if (!name) {
    errors.push(`Row ${lineNumber}: Line Item Name is required`);
  }

  // Validate dates
  const startDateStr = getMappedValue('startDate');
  if (startDateStr) {
    const startDate = parseDate(startDateStr);
    if (!startDate || !startDate.isValid()) {
      errors.push(`Row ${lineNumber}: Invalid Start Date "${startDateStr}"`);
    }
  }

  const endDateStr = getMappedValue('endDate');
  if (endDateStr) {
    const endDate = parseDate(endDateStr);
    if (!endDate || !endDate.isValid()) {
      errors.push(`Row ${lineNumber}: Invalid End Date "${endDateStr}"`);
    }
  }

  // Validate budget
  const budgetStr = getMappedValue('budgetTotal');
  if (budgetStr) {
    const budget = parseNumber(budgetStr);
    if (budget === null) {
      errors.push(`Row ${lineNumber}: Invalid Budget "${budgetStr}"`);
    }
  }

  // Validate CPM/Bid
  const cpmStr = getMappedValue('cpm');
  if (cpmStr) {
    const cpm = parseNumber(cpmStr);
    if (cpm === null) {
      errors.push(`Row ${lineNumber}: Invalid Bid/CPM "${cpmStr}"`);
    }
  }

  return { errors, warnings };
};

/**
 * Validate all rows
 */
export const validateAllRows = (rows, columnMappings) => {
  const allErrors = [];
  const allWarnings = [];

  rows.forEach((row) => {
    const { errors, warnings } = validateLineItemRow(row.data, row.lineNumber, columnMappings);
    allErrors.push(...errors);
    allWarnings.push(...warnings);
  });

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    warnings: allWarnings,
  };
};

/**
 * Transform parsed row data to line item objects
 */
export const transformToLineItems = (rows, columnMappings, campaignDefaults = {}) => {
  const {
    campaignStartDate,
    campaignEndDate,
    defaultCpm = 7,
    inheritedValueAdd = false,
  } = campaignDefaults;

  // Helper to get mapped value
  const getMappedValue = (rowData, fieldKey) => {
    const csvColumn = Object.keys(columnMappings).find(
      col => columnMappings[col]?.field === fieldKey
    );
    return csvColumn ? rowData[csvColumn]?.trim() : null;
  };

  return rows.map((row, index) => {
    const { data } = row;

    // Parse basic fields
    const name = getMappedValue(data, 'name') || `Imported Line Item ${index + 1}`;

    // Always use campaign dates for imported line items (ignore CSV dates)
    const startDate = campaignStartDate
      ? moment(campaignStartDate).utc().startOf('day')
      : moment().utc().startOf('day');

    const endDate = campaignEndDate
      ? moment(campaignEndDate).utc().endOf('day')
      : moment().add(30, 'days').utc().endOf('day');

    const budgetTotal = parseNumber(getMappedValue(data, 'budgetTotal')) || 0;
    // Use parsePercentage to handle both decimal (0.3) and percentage (30) formats
    // Keep null if not provided to distinguish from explicit 0%
    const budgetPercentage = parsePercentage(getMappedValue(data, 'budgetPercentage'));
    const cpm = parseNumber(getMappedValue(data, 'cpm')) || defaultCpm;

    // Parse segments (can be comma-separated)
    const segmentNames = getMappedValue(data, 'segments');
    const segmentIds = getMappedValue(data, 'segmentId');
    const nexxenSegments = [];

    if (segmentNames && segmentIds) {
      const names = segmentNames.split(',').map(s => s.trim()).filter(Boolean);
      const ids = segmentIds.split(',').map(s => s.trim()).filter(Boolean);

      // Match names with IDs (they should be in order)
      names.forEach((segName, i) => {
        if (ids[i]) {
          nexxenSegments.push({
            segmentId: ids[i],
            name: segName,
            segmentType: 'segment',
            cost: 0,
          });
        }
      });
    }

    // Parse deal IDs (can be comma-separated)
    const dealNames = getMappedValue(data, 'dealName');
    const dealIds = getMappedValue(data, 'dealId');
    const dspDealIds = [];

    if (dealNames && dealIds) {
      const names = dealNames.split(',').map(s => s.trim()).filter(Boolean);
      const ids = dealIds.split(',').map(s => s.trim()).filter(Boolean);

      // Match names with IDs
      names.forEach((dealName, i) => {
        if (ids[i]) {
          dspDealIds.push({
            dealId: ids[i],
            name: dealName,
            cpmBid: null,
          });
        }
      });
    }

    // Build line item object
    const lineItem = {
      name,
      startDate,
      endDate,
      budgetTotal,
      budgetPercentage,
      cpm,
      regionGroups: [],
      targetingGroups: [],
      targetingType: null,
      targetingRegions: [],
      targetingLocations: [],
      targetingCountries: null,
      audienceType: nexxenSegments.length > 0 ? 'THIRD_PARTY_DATA' : null,
      firstPartyDataAudiences: [],
      adGroup: null,
      valueAdd: inheritedValueAdd,
      nexxenSegments,
      dspDealIds,
    };

    return lineItem;
  });
};

/**
 * Normalize line item percentages after import
 * Preserves CSV percentage if valid, otherwise calculates from budgetTotal/campaignBudget
 * @param {Array} lineItems - Array of imported line items
 * @param {number} campaignBudget - The campaign's total budget
 * @returns {Array} Line items with normalized budgetPercentage values
 */
export const normalizeLineItemPercentages = (lineItems, campaignBudget) => {
  return lineItems.map(lineItem => {
    // Check if CSV provided a valid percentage (number including 0, but not null/undefined)
    const hasValidPercentage = Number.isFinite(lineItem.budgetPercentage) && lineItem.budgetPercentage >= 0;

    // Calculate percentage from budget if possible
    const calculatedPercentage = campaignBudget > 0 && lineItem.budgetTotal > 0
      ? parseFloat(((lineItem.budgetTotal / campaignBudget) * 100).toFixed(1))
      : null;

    // Priority: CSV percentage > calculated percentage > preserve original > 0
    let finalPercentage;
    if (hasValidPercentage) {
      finalPercentage = lineItem.budgetPercentage;
    } else if (calculatedPercentage !== null) {
      finalPercentage = calculatedPercentage;
    } else if (lineItem.budgetPercentage != null) {
      finalPercentage = lineItem.budgetPercentage;
    } else {
      finalPercentage = 0;
    }

    return {
      ...lineItem,
      budgetPercentage: finalPercentage,
    };
  });
};

/**
 * Create flight schedules from campaign flights with budget auto-split
 * Copies campaign flight dates and splits the line item budget equally across flights
 * @param {Array} campaignFlights - Array of campaign flight schedule objects
 * @param {number} lineItemBudget - The line item's total budget to split
 * @param {number} defaultCpm - Default CPM to use for flights
 * @returns {Array} Array of flight schedule objects with budget split equally
 */
export const createFlightSchedulesWithBudget = (campaignFlights, lineItemBudget, defaultCpm) => {
  if (!campaignFlights?.length) return [];

  const numFlights = campaignFlights.length;
  const budget = lineItemBudget || 0;
  const baseBudgetPerFlight = Math.floor((budget / numFlights) * 100) / 100;
  const remainder = Math.round((budget - baseBudgetPerFlight * numFlights) * 100);

  return campaignFlights.map((flight, index) => {
    // Distribute remainder cents to first flights
    const flightBudget = index < remainder ? baseBudgetPerFlight + 0.01 : baseBudgetPerFlight;
    return {
      startDate: flight.startDate ? moment(flight.startDate) : null,
      endDate: flight.endDate ? moment(flight.endDate) : null,
      budget: parseFloat(flightBudget.toFixed(2)),
      cpm: flight.cpm || defaultCpm || 7,
    };
  });
};
